import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { language, setLanguage, t } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { href: '/', label: t('nav.home') },
    { href: '/about', label: t('nav.about') },
    { href: '/services', label: language === 'en' ? 'Services' : 'Услуги' },
    { href: '/fleets', label: t('nav.fleets') },
    { href: '/careers', label: language === 'en' ? 'Careers' : 'Карьера' },
    { href: '/contact', label: t('nav.contact') },
  ];

  return (
    <>
      {/* Fixed Navbar */}
      <nav
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled ? 'bg-white/95 backdrop-blur-sm shadow-sm' : 'bg-gradient-to-b from-black/50 to-transparent'
        }`}
      >
        <div className="container-bespoke">
          <div className="flex items-center justify-between h-24">
            {/* Logo */}
            <Link
              to="/"
              data-testid="logo-link"
              className="relative z-10"
            >
              <img 
                src="/logo.png" 
                alt="Rota Maritime Agency" 
                className={`h-20 w-auto object-contain transition-all duration-300 ${
                  isScrolled ? '' : 'brightness-0 invert'
                }`}
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-12">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  data-testid={`nav-link-${link.href.replace('/', '') || 'home'}`}
                  className={`text-editorial transition-colors duration-300 ${
                    isScrolled 
                      ? isActive(link.href) ? 'text-signal' : 'text-navy hover:text-signal'
                      : isActive(link.href) ? 'text-signal' : 'text-white hover:text-signal drop-shadow-md'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Right side */}
            <div className="hidden lg:flex items-center gap-6">
              <button
                onClick={() => setLanguage(language === 'en' ? 'ru' : 'en')}
                data-testid="language-switcher"
                className={`text-editorial transition-colors flex items-center gap-2 ${
                  isScrolled ? 'text-navy hover:text-signal' : 'text-white hover:text-signal drop-shadow-md'
                }`}
              >
                <Globe className="w-4 h-4" />
                {language.toUpperCase()}
              </button>
              <Link to="/contact">
                <button data-testid="nav-cta" className="btn-signal">
                  {language === 'en' ? 'Get Quote' : 'Запрос'}
                </button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-toggle"
              className={`lg:hidden relative z-10 ${isScrolled ? 'text-navy' : 'text-white'}`}
              onClick={() => setIsOpen(!isOpen)}
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Full Screen Mobile Menu */}
      {isOpen && (
        <div
          data-testid="mobile-menu"
          className="fixed inset-0 z-40 bg-white flex items-center justify-center"
        >
          <div className="text-center space-y-8">
            {navLinks.map((link, index) => (
              <div key={link.href} className="overflow-hidden">
                <Link
                  to={link.href}
                  onClick={() => setIsOpen(false)}
                  className={`block text-4xl sm:text-5xl font-heading font-extrabold animate-fade-in-up ${
                    isActive(link.href) ? 'text-signal' : 'text-navy'
                  }`}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  {link.label}
                </Link>
              </div>
            ))}
            <div className="pt-8 animate-fade-in-up" style={{ animationDelay: '500ms' }}>
              <button
                onClick={() => {
                  setLanguage(language === 'en' ? 'ru' : 'en');
                  setIsOpen(false);
                }}
                className="text-editorial text-slate-500 hover:text-navy"
              >
                {language === 'en' ? 'Русский' : 'English'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
